import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { RootState } from "setup";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { ClientPortalLinkedClientAccountModel, ClientPortalUserModel } from "services/api/cplauthapi/CPLAuthAPI";
import { IClientPortalUsersService } from "services/ClientPortalUsersService";
import translations from "utils/translationUtility";

type ClientPortalUserFormState = {
  ClientPortalUser: ClientPortalUserModel;
  LinkedClientAccounts: ClientPortalLinkedClientAccountModel[];
  EditingClientPortalUser: boolean;
  LoadingClientPortalUser: boolean;
};

const initialState: ClientPortalUserFormState = {
  ClientPortalUser: new ClientPortalUserModel({ firstName: "", lastName: "", emailAddress: "" }),
  LinkedClientAccounts: [],
  EditingClientPortalUser: false,
  LoadingClientPortalUser: false,
};

export const clientPortalUserSlice = createSlice({
  name: "clientPortalUser",
  initialState,
  reducers: {
    resetData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getClientPortalUser.pending, (state) => {
        state.LoadingClientPortalUser = true;
      })
      .addCase(getClientPortalUser.rejected, (state) => {
        state.LoadingClientPortalUser = false;
      })
      .addCase(getClientPortalUser.fulfilled, (state, action) => {
        state.LoadingClientPortalUser = false;
        state.ClientPortalUser = action.payload;
      })
      .addCase(getClientPortalUserLinkedClientAccounts.pending, (state) => {
        state.LoadingClientPortalUser = true;
      })
      .addCase(getClientPortalUserLinkedClientAccounts.rejected, (state) => {
        state.LoadingClientPortalUser = false;
      })
      .addCase(getClientPortalUserLinkedClientAccounts.fulfilled, (state, action) => {
        state.LoadingClientPortalUser = false;
        state.LinkedClientAccounts = action.payload;
      })
      .addCase(deleteClientPortalUserClientAccountLink.pending, (state) => {
        state.EditingClientPortalUser = true;
      })
      .addCase(deleteClientPortalUserClientAccountLink.rejected, (state) => {
        state.EditingClientPortalUser = false;
        toast.error("Failed to delete client account link");
      })
      .addCase(deleteClientPortalUserClientAccountLink.fulfilled, (state) => {
        state.EditingClientPortalUser = false;
        toast.success(`${translations.client} account link deleted successfully`);
      })
      .addCase(setClientPortalUserPasswordChangeRequired.pending, (state) => {
        state.EditingClientPortalUser = true;
      })
      .addCase(setClientPortalUserPasswordChangeRequired.rejected, (state) => {
        state.EditingClientPortalUser = false;
        toast.error("Failed to set password change required");
      })
      .addCase(setClientPortalUserPasswordChangeRequired.fulfilled, (state) => {
        state.EditingClientPortalUser = false;
        toast.success("Successfully set password change required");
      })
      .addCase(enableClientPortalUser.pending, (state) => {
        state.EditingClientPortalUser = true;
      })
      .addCase(enableClientPortalUser.rejected, (state) => {
        state.EditingClientPortalUser = false;
        toast.error(`${translations.resident} portal user failed to enable`);
      })
      .addCase(enableClientPortalUser.fulfilled, (state) => {
        state.EditingClientPortalUser = false;
        toast.success(`${translations.resident} portal user enabled successfully`);
      })
      .addCase(disableClientPortalUser.pending, (state) => {
        state.EditingClientPortalUser = true;
      })
      .addCase(disableClientPortalUser.rejected, (state) => {
        state.EditingClientPortalUser = false;
        toast.error(`${translations.resident} portal user failed to disable`);
      })
      .addCase(disableClientPortalUser.fulfilled, (state) => {
        state.EditingClientPortalUser = false;
        toast.success(`${translations.resident} portal user disabled successfully`);
      });
  },
});

export const { resetData } = clientPortalUserSlice.actions;

export const getClientPortalUser = createAsyncThunk(
  "ClientPortalUser/GetClientPortalUser",
  async (clientPortalUserID: string) => {
    const clientPortalUsersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
    return await clientPortalUsersService.getClientPortalUser(clientPortalUserID);
  }
);

export const getClientPortalUserLinkedClientAccounts = createAsyncThunk<
  ClientPortalLinkedClientAccountModel[],
  { clientPortalUserID: string },
  { state: RootState }
>("ClientPortalUser/GetClientPortalUserLinkedClientAccounts", async ({ clientPortalUserID }, thunkAPI) => {
  const clientPortalUsersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
  const state = thunkAPI.getState();
  return await clientPortalUsersService.getLinkedClientAccounts(clientPortalUserID, state.auth.user!.companyID ?? "");
});

export const deleteClientPortalUserClientAccountLink = createAsyncThunk<
  void,
  { clientPortalUserMappingID: number; clientPortalUserID: string },
  { state: RootState }
>(
  "ClientPortalUser/DeleteClientPortalUserLinkedClientAccount",
  async ({ clientPortalUserMappingID, clientPortalUserID }, thunkAPI) => {
    const clientPortalUsersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
    await clientPortalUsersService.deleteClientAccountLink(clientPortalUserMappingID);
    thunkAPI.dispatch(getClientPortalUserLinkedClientAccounts({ clientPortalUserID: clientPortalUserID }));
  }
);

export const enableClientPortalUser = createAsyncThunk(
  "ClientPortalUser/EnableClientPortalUser",
  async (clientPortalUserID: string, thunkAPI) => {
    const clientPortalUsersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
    await clientPortalUsersService.enableClientPortalUser(clientPortalUserID);
    thunkAPI.dispatch(getClientPortalUser(clientPortalUserID));
  }
);

export const disableClientPortalUser = createAsyncThunk(
  "ClientPortalUser/DisableClientPortalUser",
  async (clientPortalUserID: string, thunkAPI) => {
    const clientPortalUsersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
    await clientPortalUsersService.disableClientPortalUser(clientPortalUserID);
    thunkAPI.dispatch(getClientPortalUser(clientPortalUserID));
  }
);

export const setClientPortalUserPasswordChangeRequired = createAsyncThunk<
  void,
  { passwordChangeRequired: boolean; clientPortalUserID: string },
  { state: RootState }
>(
  "ClientPortalUser/SetClientPortalUserPasswordChangeRequired",
  async ({ passwordChangeRequired, clientPortalUserID }, thunkAPI) => {
    const clientPortalUsersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
    await clientPortalUsersService.setPasswordChangeRequired(passwordChangeRequired, clientPortalUserID);
    thunkAPI.dispatch(getClientPortalUser(clientPortalUserID));
  }
);

export const selectLoadingClientPortalUser = (state: RootState) => {
  return state.clientPortalUser.LoadingClientPortalUser;
};

export const selectEditingClientPortalUser = (state: RootState) => {
  return state.clientPortalUser.EditingClientPortalUser;
};

export const selectClientPortalUser = (state: RootState) => {
  return state.clientPortalUser.ClientPortalUser;
};

export const selectLinkedClientAccounts = (state: RootState) => {
  return state.clientPortalUser.LinkedClientAccounts;
};

export default clientPortalUserSlice.reducer;
